/**
 * useFocusTrap — move focus into an open dialog and keep Tab inside it.
 *
 * Without a trap, tabbing out of a modal walks into the map controls
 * behind the backdrop, which a screen-reader user cannot tell is
 * covered. Also lands focus on the container itself on open, so the
 * dialog's own label is announced before any control inside it. The
 * container therefore needs `tabIndex={-1}`, or `.focus()` is a silent
 * no-op and the trap does nothing on entry while still trapping Tab.
 *
 * Escape is deliberately not handled here — useEscapeKey owns that, and
 * see its header for why stacking there is the consumer's job.
 *
 * Nesting is handled here instead, because every keydown listener on the
 * document runs regardless of what the others do. The module-level stack
 * below means only the innermost open dialog acts on Tab; without it the
 * detail sheet's trap fights the lightbox opened on top of it and pulls
 * focus back out of the thing the reader just opened.
 *
 * Pair with useFocusRestore, which owns the way back out, and call that
 * one first. Cleanups run in declaration order and StrictMode mounts
 * twice, so trapping before capturing makes the dialog record itself as
 * the place to send focus back to. src/hooks/focusOrder.test.ts checks it.
 *
 * Consumers: ui/Modal, WelcomeSheet, DetailSheetWrapper, FolkloreDetail,
 * WikiArticleViewer, FullscreenViewer, ExploreMode, DetailErrorBoundary.
 */
import { useEffect } from "react";
import type React from "react";

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Innermost open dialog last. Only its trap responds to Tab. */
const stack: HTMLElement[] = [];

export function useFocusTrap(ref: React.RefObject<HTMLElement | null>, enabled = true): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    stack.push(el);
    el.focus({ preventScroll: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (stack[stack.length - 1] !== el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      // Shift-Tab from the container itself wraps to the end, otherwise
      // the first backwards Tab escapes before the cycle ever starts.
      if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (active instanceof Node && !el.contains(active)) {
        // Focus was already outside when Tab was pressed, which happens when
        // the dialog opened from a control that kept focus. Pull it in rather
        // than letting the reader tab through a subtree that aria-modal has
        // told their software to ignore.
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const i = stack.lastIndexOf(el);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [ref, enabled]);
}
